import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { getDisplayPrice } from 'components/marketplace/helpers/displayPrice';
import { buildGalleryPhotos } from 'components/marketplace/helpers/galleryPhotos';
import { dollarsToCents } from 'components/marketplace/helpers/money';
import { useMarketplaceFavorite } from 'components/marketplace/helpers/useMarketplaceFavorite';
import {
  buildPropertyAnalyticsValue,
  PropertyAnalytics,
  useTrackProperty,
} from 'components/marketplace/helpers/useTrackProperty';
import { loadPropertyPage } from 'components/marketplace/property/details/loadPropertyPage';
import type { PropertyPageProps } from 'components/marketplace/property/details/loadPropertyPage';
import { GalleryPhotos } from 'components/marketplace/property/media/GalleryPhotos';
import {
  heroWalkthroughVideoUrl,
  pdpHeroMedia,
  pdpMediaAvailability,
  shouldMountHeroTour3d,
} from 'components/marketplace/property/media/pdpEnhancedMedia';
import { PhotoCarousel } from 'components/marketplace/property/media/PhotoCarousel';
import { usePdpMediaSlot } from 'components/marketplace/property/media/usePdpMediaSlot';
import { PdpAnalytics } from 'components/marketplace/property/PdpAnalytics';
import { PropertySection } from 'components/marketplace/property/PropertySection';
import { PdpRefreshExposure } from 'components/marketplace/property/refresh/PdpRefreshExposure';
import {
  headerValue,
  PDP_REFRESH_ARM_HEADER,
  PDP_REFRESH_VARIATION_HEADER,
  pdpRefreshFromEdge,
} from 'components/marketplace/property/refresh/release';
import type { PropertyDetailsType } from 'components/marketplace/property/types';
import MarketplaceLayout from 'components/properties/shared/MarketplaceLayout';
import type {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from 'declarations/shared/serverSideProps';
import dynamic from 'next/dynamic';

import { useObservability } from '../../../../helpers/observability';

const Lightbox = dynamic(
  () => import('components/marketplace/property/Lightbox').then((mod) => mod.Lightbox),
  {
    ssr: false,
  },
);

const PdpRefresh = dynamic(() =>
  import('components/marketplace/property/refresh/PdpRefresh').then((module) => module.PdpRefresh),
);

const ShareModal = dynamic(
  () => import('components/marketplace/property/ShareModal').then((mod) => mod.ShareModal),
  {
    ssr: false,
  },
);

const Page: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const property = props.property;
  const analyticsValue = useMemo(() => buildPropertyAnalyticsValue(property), [property]);
  return (
    <PdpAnalytics
      addressId={property.addressToken}
      refreshed={props.pdpRefreshEnabled}
      preview={props.preview ?? false}
    >
      <PdpRefreshExposure
        addressId={property.addressToken}
        experiment={props.pdpRefreshExperiment}
        preview={props.preview ?? false}
      />
      <PropertyAnalytics.Provider value={analyticsValue}>
        <PageInner {...props} />
      </PropertyAnalytics.Provider>
    </PdpAnalytics>
  );
};

const PageInner: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  property,
  pdpRefreshEnabled,
  engagement,
  preview = false,
}) => {
  const { trackProperty } = useTrackProperty();
  // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
  const { trackEvent, trackJourneyEvent } = useObservability();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [shareModalState, setShareModalState] = useState<'unopened' | 'open' | 'closed'>(
    'unopened',
  );
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const toggleLightbox = useCallback(() => setIsLightboxOpen((prev) => !prev), []);
  // bricks-next `Lightbox` registers a document-level keydown listener that
  // fires `handleClose()` on Escape regardless of `isOpen`, so `handleClose`
  // must be close-only rather than a toggle.
  const closeLightbox = useCallback(() => setIsLightboxOpen(false), []);
  const handleSetSelectedPhotoIndex = useCallback((idx: number) => setSelectedPhotoIndex(idx), []);
  const { isFavoriteLoading, isLiked, onToggleFavorite } = useMarketplaceFavorite({
    addressId: property.addressToken,
    origin: 'LISTING_DETAILS',
    priceCents: property.listPrice == null ? null : dollarsToCents(property.listPrice),
  });
  const handleToggleFavorite = useCallback(() => {
    onToggleFavorite();
  }, [onToggleFavorite]);
  const marketplaceProperty = useMemo<PropertyDetailsType>(
    () => ({
      ...property,
      isFavoriteLoading,
      isLiked,
      onToggleFavorite: handleToggleFavorite,
    }),
    [handleToggleFavorite, isFavoriteLoading, isLiked, property],
  );

  const formattedAddress = useMemo(
    () => formatStreetAddress(property.street, property.unitType, property.unitNumber),
    [property.street, property.unitType, property.unitNumber],
  );

  const carouselPhotos = useMemo(() => buildGalleryPhotos(property), [property]);
  const openShareModal = useCallback(() => setShareModalState('open'), []);
  const closeShareModal = useCallback(() => setShareModalState('closed'), []);
  const walkthroughVideoUrl = useMemo(() => heroWalkthroughVideoUrl(property), [property]);
  const availability = useMemo(
    () => pdpMediaAvailability(walkthroughVideoUrl, property.tour3d),
    [property.tour3d, walkthroughVideoUrl],
  );
  const mediaSlot = usePdpMediaSlot({ addressToken: property.addressToken, availability });
  const showTour3d = shouldMountHeroTour3d(property.tour3d, mediaSlot.assignedState);
  const displayTour3d = showTour3d || preview;
  const galleryTour3dUrl = pdpRefreshEnabled && displayTour3d ? property.tour3d?.url : undefined;
  const heroMediaFill = useMemo(
    () =>
      pdpHeroMedia(property, mediaSlot, pdpRefreshEnabled ? 'gallery-pill' : 'fill', {
        galleryTour3dUrl,
      }),
    [galleryTour3dUrl, mediaSlot, pdpRefreshEnabled, property],
  );
  const heroMediaControl = useMemo(
    () => pdpHeroMedia(property, mediaSlot, 'control'),
    [mediaSlot, property],
  );

  useEffect(() => {
    if (preview) return;
    trackProperty({ featureName: 'property-view', label: 'marketplace-pdp' });
    trackJourneyEvent('marketplaceProperty', 'detailPageView', {
      addressUuid: property.addressToken,
    });
    // Segment event name only: the buyer/agent/other survey itself is configured
    // in the Sprig dashboard, including its once-per-visitor frequency capping.
    trackEvent('property-view-sprig');
  }, [preview, property.addressToken, trackEvent, trackJourneyEvent, trackProperty]);

  return (
    <MarketplaceLayout property={marketplaceProperty} fullPageScroll={pdpRefreshEnabled}>
      {pdpRefreshEnabled ? (
        <PdpRefresh
          property={marketplaceProperty}
          engagement={engagement}
          heroOverlay={heroMediaFill}
          showTour3d={displayTour3d && !heroMediaFill}
        />
      ) : (
        <Col className="w-screen overflow-hidden max-md:overflow-y-scroll md:flex-row md:h-[calc(100vh-72px-var(--site-promo-banner-height,0px))]">
          <GalleryPhotos
            handleSetSelectedPhotoIndex={handleSetSelectedPhotoIndex}
            property={marketplaceProperty}
            toggleLightbox={toggleLightbox}
            heroOverlay={heroMediaFill}
          />
          <PhotoCarousel
            isAvailableSoon={marketplaceProperty.listingState === 'AVAILABLE_SOON'}
            photos={carouselPhotos}
            price={getDisplayPrice(marketplaceProperty, 'purchase')}
            address={formattedAddress}
            toggleLightbox={toggleLightbox}
            setSelectedPhotoIndex={handleSetSelectedPhotoIndex}
            latitude={marketplaceProperty.latitude}
            longitude={marketplaceProperty.longitude}
            openShareModal={openShareModal}
            heroOverlay={heroMediaControl}
          />
          <PropertySection property={marketplaceProperty} />
        </Col>
      )}
      {isLightboxOpen && (
        <Lightbox
          handleClose={closeLightbox}
          isOpen={isLightboxOpen}
          property={marketplaceProperty}
          selectedPhotoIndex={selectedPhotoIndex}
          priceQuote={pdpRefreshEnabled ? 'listed' : 'purchase'}
        />
      )}
      {!pdpRefreshEnabled && shareModalState !== 'unopened' && (
        <ShareModal
          property={marketplaceProperty}
          isOpen={shareModalState === 'open'}
          onClose={closeShareModal}
        />
      )}
    </MarketplaceLayout>
  );
};

export const getServerSideProps: CacheSafeGetServerSideProps<PropertyPageProps> = (context) => {
  const decision = pdpRefreshFromEdge({
    releaseValue: process.env.COSMOS_PDP_REFRESH_ENABLED,
    armHeader: headerValue(context.req.headers[PDP_REFRESH_ARM_HEADER]),
    variationHeader: headerValue(context.req.headers[PDP_REFRESH_VARIATION_HEADER]),
  });
  return loadPropertyPage({
    context,
    pdpRefreshEnabled: decision.enabled,
    pdpRefreshExperiment: decision.experiment,
  });
};

export default Page;
